import React, { useState } from 'react';
import CampaignList from './CampaignList';
import CampaignForm from './CampaignForm';
import { Campaign } from '../../types/socialMediaPlanner';

interface CampaignManagerProps {
  className?: string;
}

/**
 * Campaign management component that provides a complete UI for managing social media campaigns
 */
const CampaignManager: React.FC<CampaignManagerProps> = ({ className = '' }) => {
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleSelectCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setIsEditing(false); // Close edit form if open
  };

  const handleCreateClick = () => {
    setSelectedCampaign(null);
    setIsCreating(true);
  };

  const handleEditClick = () => {
    if (selectedCampaign) {
      setIsEditing(true);
    }
  };

  const handleSaveCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleCancelForm = () => {
    setIsCreating(false);
    setIsEditing(false);
  };

  return (
    <div className={`${className}`}>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-2/3">
          {isCreating ? (
            <CampaignForm 
              onSave={handleSaveCampaign}
              onCancel={handleCancelForm}
            />
          ) : isEditing && selectedCampaign ? (
            <CampaignForm 
              campaign={selectedCampaign}
              onSave={handleSaveCampaign}
              onCancel={handleCancelForm}
            />
          ) : (
            <CampaignList
              onSelectCampaign={handleSelectCampaign}
              onCreateCampaign={handleCreateClick}
            />
          )}
        </div>
        
        <div className="lg:w-1/3">
          {selectedCampaign && !isCreating && !isEditing && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{selectedCampaign.name}</h3>
                <button 
                  onClick={handleEditClick}
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition"
                >
                  Edit Campaign
                </button>
              </div>
              
              <div className="space-y-4">
                {selectedCampaign.description && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                    <p className="text-gray-600">{selectedCampaign.description}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Status</h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    selectedCampaign.status === 'active' ? 'bg-green-100 text-green-800' :
                    selectedCampaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                    selectedCampaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedCampaign.status}
                  </span>
                </div>
                
                {selectedCampaign.tags && selectedCampaign.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCampaign.tags.map((tag, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedCampaign.goal && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Campaign Goal</h4>
                    <div className="bg-gray-50 p-3 rounded">
                      <span className="font-medium">{selectedCampaign.goal}</span>
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Posts</h4>
                  <p className="text-gray-600">
                    {selectedCampaign.posts?.length 
                      ? `This campaign contains ${selectedCampaign.posts.length} posts.` 
                      : 'No posts have been added to this campaign yet.'}
                  </p>
                  {selectedCampaign.posts?.length > 0 && (
                    <button 
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                      onClick={() => {
                        // This would link to campaign posts view
                        console.log('View campaign posts clicked');
                      }}
                    >
                      View Campaign Posts
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignManager;
